import { UserSystemGroupStrings } from 'store/storeTypes';
import { WorkingDay } from '../WorkingHours.types';
import { timeSpanToMinutes } from './slotLabelUtils';
import {
  ProductionHoursConfigurationApiResponse,
  ProductionHoursDay as ApiDay,
} from './productionHoursApiTypes';
import { DAY_OF_WEEK_NAMES, systemGroupTypeFromApi } from './productionHoursEnumMaps';

// Our day ids/names, Monday-first (matches workingHoursReducer's INITIAL_WORKING_HOURS_STATE).
const OUR_DAYS = [
  { id: 'monday', name: 'Monday' },
  { id: 'tuesday', name: 'Tuesday' },
  { id: 'wednesday', name: 'Wednesday' },
  { id: 'thursday', name: 'Thursday' },
  { id: 'friday', name: 'Friday' },
  { id: 'saturday', name: 'Saturday' },
  { id: 'sunday', name: 'Sunday' },
];

// Response shifts don't map to a `shiftName` field in our local TimeSlot model (we don't
// persist custom shift names yet — labels are still derived alphabetically by position),
// so shiftName is intentionally dropped here.
const apiDaysToWorkingDays = (apiDays: ApiDay[]): WorkingDay[] => {
  const byName = new Map(apiDays.map(day => [DAY_OF_WEEK_NAMES[day.dayOfWeek], day]));
  return OUR_DAYS.map(({ id, name }) => {
    const apiDay = byName.get(name);
    if (!apiDay) return { id, name, enabled: false, slots: [] };
    return {
      id,
      name,
      enabled: apiDay.isEnabled,
      slots: apiDay.shifts.map((shift, index) => ({
        id: `${id}-api-${index}`,
        startMinutes: timeSpanToMinutes(shift.startTime),
        endMinutes: timeSpanToMinutes(shift.endTime),
      })),
    };
  });
};

export type ConfigurationBySystemGroupType = Partial<Record<UserSystemGroupStrings, WorkingDay[]>>;

export const mapConfigurationResponse = (
  response: ProductionHoursConfigurationApiResponse
): { bySystemGroupType: ConfigurationBySystemGroupType; isMixed: boolean } => {
  const entries = Array.isArray(response) ? response : [response];
  const bySystemGroupType: ConfigurationBySystemGroupType = {};
  let isMixed = false;

  entries.forEach(entry => {
    if (entry?.isMixed) isMixed = true;
    entry?.configurationsPerSystemGroupType?.forEach(config => {
      const groupKey = systemGroupTypeFromApi(config.systemGroupType);
      if (!groupKey) return;
      bySystemGroupType[groupKey] = apiDaysToWorkingDays(config.days);
    });
  });

  return { bySystemGroupType, isMixed };
};
